// components/ui/ErrorState.tsx
import { AlertTriangle, RefreshCw, WifiOff, Printer, ServerCrash } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from './Card';

interface ErrorStateProps {
    title?: string;
    message?: string;
    onRetry?: () => void;
    onBack?: () => void;
    variant?: 'error' | 'offline' | 'printer' | 'server';
}

export function ErrorState({
    title = 'Something went wrong',
    message = 'An unexpected error occurred. Please try again.',
    onRetry,
    onBack,
    variant = 'error'
}: ErrorStateProps) {
    const configs: Record<string, {
        icon: React.ElementType;
        color: string;
        borderColor: string;
        bgGradient: string;
    }> = {
        error: {
            icon: AlertTriangle,
            color: 'text-rose-500',
            borderColor: 'border-rose-200',
            bgGradient: 'from-rose-50 to-rose-100/50',
        },
        offline: {
            icon: WifiOff,
            color: 'text-amber-500',
            borderColor: 'border-amber-200',
            bgGradient: 'from-amber-50 to-amber-100/50',
        },
        printer: {
            icon: Printer,
            color: 'text-violet-500',
            borderColor: 'border-violet-200',
            bgGradient: 'from-violet-50 to-violet-100/50',
        },
        server: {
            icon: ServerCrash,
            color: 'text-slate-500',
            borderColor: 'border-slate-200',
            bgGradient: 'from-slate-50 to-slate-100/50',
        },
    };

    const config = configs[variant];
    const Icon = config.icon;

    return (
        <Card className="border-2 border-dashed overflow-hidden">
            <div className="flex flex-col items-center justify-center py-16 text-center px-6 relative">
                <div className={`absolute inset-0 bg-gradient-to-br ${config.bgGradient} opacity-50`} />

                <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="relative z-10"
                >
                    <motion.div
                        animate={{
                            boxShadow: [
                                '0 0 0 0px rgba(239, 68, 68, 0)',
                                '0 0 0 20px rgba(239, 68, 68, 0)',
                            ],
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={`w-20 h-20 rounded-2xl bg-white shadow-xl flex items-center justify-center mb-6 ${config.borderColor} border-2`}
                    >
                        <motion.div
                            animate={{ rotate: [0, 5, -5, 0] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                        >
                            <Icon className={`w-10 h-10 ${config.color}`} />
                        </motion.div>
                    </motion.div>
                </motion.div>

                <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative z-10 text-xl font-black text-slate-900"
                >
                    {title}
                </motion.h3>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="relative z-10 text-sm text-slate-500 mt-2 max-w-md leading-relaxed"
                >
                    {message}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="relative z-10 flex items-center gap-3 mt-6"
                >
                    {onRetry && (
                        <button
                            onClick={onRetry}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold text-sm shadow-lg shadow-emerald-500/25 whitespace-nowrap"
                            type="button"
                        >
                            <RefreshCw size={16} className="flex-shrink-0" />
                            Try Again
                        </button>
                    )}
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all font-medium text-sm text-slate-700 shadow-sm whitespace-nowrap"
                            type="button"
                        >
                            Go Back
                        </button>
                    )}
                </motion.div>
            </div>
        </Card>
    );
}